import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { REPORTS_DATABASE_OBJECT_STORE_NAME } from '@/core/constants/reportsDatabase';
import {
  ReportReadStatus,
  ReportSourceType,
  ReportsDatabaseUserReports,
} from '@/core/interfaces/common';

import { getUserSelector } from '@/features/Auth/store';
import { Report } from '@/features/Reports/interfaces';
import { changeReportReadStatus } from '@/features/Reports/store';

import { getReportDatabaseConnection } from '@/utils/getReportsDatabaseResults';

import { useAppDispatch } from '../store/store';

export const useReportReadStatus = () => {
  const auth = useSelector(getUserSelector);
  const [db, setDb] = useState<IDBDatabase | null>(null);
  const dispatch = useAppDispatch();

  const markReportInBrowser = useCallback(
    (report: Report, status: ReportReadStatus) => {
      if (!auth?.id || !db) {
        return;
      }

      const reportId = report.reportId;
      const authId = auth.id;
      const reportSourceType = report.reportSourceType;

      const reportsObjectStoreTransaction = db
        .transaction(REPORTS_DATABASE_OBJECT_STORE_NAME, 'readwrite')
        .objectStore(REPORTS_DATABASE_OBJECT_STORE_NAME);

      const reportsUserRequest = reportsObjectStoreTransaction.get(authId);

      reportsUserRequest.onsuccess = () => {
        let lookoutReportIds: ReportsDatabaseUserReports['lookoutReportIds'] = [];
        let horizonReportIds: ReportsDatabaseUserReports['horizonReportIds'] = [];

        if (reportsUserRequest.result) {
          lookoutReportIds = (reportsUserRequest.result as ReportsDatabaseUserReports)
            .lookoutReportIds;
          horizonReportIds = (reportsUserRequest.result as ReportsDatabaseUserReports)
            .horizonReportIds;
        }

        if (status === ReportReadStatus.READ) {
          if (reportSourceType === ReportSourceType.LOOKOUT) {
            lookoutReportIds.push(reportId);
          } else if (reportSourceType === ReportSourceType.HORIZON) {
            horizonReportIds.push(reportId);
          }
        } else if (status === ReportReadStatus.UNREAD) {
          if (reportSourceType === ReportSourceType.LOOKOUT) {
            lookoutReportIds.filter((savedReportId: number) => savedReportId !== reportId);
          } else if (reportSourceType === ReportSourceType.HORIZON) {
            horizonReportIds.filter((savedReportId: number) => savedReportId !== reportId);
          }
        }

        const updatedReportsUser: ReportsDatabaseUserReports = {
          authId,
          lookoutReportIds,
          horizonReportIds,
        };

        reportsObjectStoreTransaction.put(updatedReportsUser);
        dispatch(
          changeReportReadStatus({
            readStatus: status,
            reportId,
            reportSourceType,
          })
        );
      };
    },
    [auth?.id, db, dispatch]
  );

  useEffect(() => {
    if (!db) {
      getReportDatabaseConnection(setDb);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    markReportInBrowser,
  };
};
